import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { authAPI } from '@/lib/api';

const Register = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    role: 'buyer',
    business_type: 'individual',
    phone: '',
    company_name: '',
    country: '',
    province: '',
    town: '',
    district: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!acceptedTerms) {
      toast.error('Please accept the Terms and Conditions to continue');
      return;
    }
    
    setLoading(true);

    try {
      const response = await authAPI.register(formData);
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      
      toast.success('Registration successful!');
      
      // Navigate based on role
      const role = response.data.user.role;
      if (role === 'admin') {
        navigate('/admin/dashboard');
      } else if (role === 'seller') {
        navigate('/seller/dashboard');
      } else {
        navigate('/buyer/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-surface flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl p-8">
        <div className="text-center mb-8">
          <img src="/servpac-logo.png" alt="ServPac" className="w-16 h-16 mx-auto mb-4 object-contain" />
          <h1 data-testid="register-heading" className="text-3xl font-chivo font-bold text-ocean-deep mb-2">Create Account</h1>
          <p className="text-slate-600">Join ServPac today</p>
        </div>

        <form data-testid="register-form" onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Label htmlFor="full_name">Full Name</Label>
              <Input
                data-testid="register-name-input"
                id="full_name"
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                required
              />
            </div>

            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                data-testid="register-email-input"
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
              />
            </div>

            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                data-testid="register-password-input"
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
              />
            </div>

            <div>
              <Label htmlFor="phone">Phone (Optional)</Label>
              <Input
                data-testid="register-phone-input"
                id="phone"
                type="tel"
                placeholder="+1234567890"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
            </div>

            <div>
              <Label htmlFor="role">I am a</Label>
              <Select
                value={formData.role}
                onValueChange={(value) => setFormData({ ...formData, role: value })}
              >
                <SelectTrigger data-testid="register-role-select" id="role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="buyer">Buyer</SelectItem>
                  <SelectItem value="seller">Seller</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="business_type">Account Type</Label>
              <Select
                value={formData.business_type}
                onValueChange={(value) => setFormData({ ...formData, business_type: value })}
              >
                <SelectTrigger data-testid="register-business-type-select" id="business_type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="individual">Individual</SelectItem>
                  <SelectItem value="business">Business</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {formData.business_type === 'business' && (
              <div className="md:col-span-2">
                <Label htmlFor="company_name">Company Name</Label>
                <Input
                  data-testid="register-company-input"
                  id="company_name"
                  value={formData.company_name}
                  onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                />
              </div>
            )}

            {formData.role === 'seller' && (
              <>
                <div className="md:col-span-2">
                  <h4 className="text-lg font-chivo font-semibold text-slate-900 mb-4">
                    Location Details (Optional)
                  </h4>
                </div>

                <div>
                  <Label htmlFor="country">Country</Label>
                  <Input
                    data-testid="register-country-input"
                    id="country"
                    placeholder="e.g., New Zealand"
                    value={formData.country}
                    onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                  />
                </div>

                <div>
                  <Label htmlFor="province">Province/State</Label>
                  <Input
                    data-testid="register-province-input"
                    id="province"
                    placeholder="e.g., Auckland"
                    value={formData.province}
                    onChange={(e) => setFormData({ ...formData, province: e.target.value })}
                  />
                </div>

                <div>
                  <Label htmlFor="town">Town/City</Label>
                  <Input
                    data-testid="register-town-input"
                    id="town"
                    placeholder="e.g., Wellington"
                    value={formData.town}
                    onChange={(e) => setFormData({ ...formData, town: e.target.value })}
                  />
                </div>

                <div>
                  <Label htmlFor="district">District/Area/Ward</Label>
                  <Input
                    data-testid="register-district-input"
                    id="district"
                    placeholder="e.g., Central District"
                    value={formData.district}
                    onChange={(e) => setFormData({ ...formData, district: e.target.value })}
                  />
                </div>
              </>
            )}
          </div>

          {/* Terms and Conditions */}
          <div className="flex items-start space-x-3 p-4 bg-slate-50 rounded-lg border">
            <Checkbox
              id="terms"
              checked={acceptedTerms}
              onCheckedChange={(checked) => setAcceptedTerms(checked)}
              data-testid="terms-checkbox"
            />
            <div className="grid gap-1.5 leading-none">
              <label
                htmlFor="terms"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
              >
                I accept the Terms and Conditions
              </label>
              <p className="text-xs text-slate-500">
                By creating an account, you agree to our{' '}
                <a href="/terms" target="_blank" className="text-ocean-deep hover:underline">
                  Terms of Service
                </a>{' '}
                and{' '}
                <a href="/privacy" target="_blank" className="text-ocean-deep hover:underline">
                  Privacy Policy
                </a>
                . You confirm that you are at least 18 years old.
              </p>
            </div>
          </div>

          <Button
            data-testid="register-submit-btn"
            type="submit"
            className="w-full"
            disabled={loading || !acceptedTerms}
          >
            {loading ? 'Creating account...' : 'Create Account'}
          </Button>
        </form>

        <div className="mt-6 text-center text-sm">
          <span className="text-slate-600">Already have an account? </span>
          <Link data-testid="register-login-link" to="/login" className="text-slate-900 font-medium hover:underline">
            Sign in here
          </Link>
        </div>

        <div className="mt-4 text-center">
          <Link data-testid="register-home-link" to="/" className="text-sm text-slate-600 hover:underline">
            ← Back to home
          </Link>
        </div>
      </Card>
    </div>
  );
};

export default Register;